/**
 * CARD-TO-CARD DEPENDENCIES — the pure arithmetic of "this one waits on that one".
 *
 * A dependency is a FINISH-TO-START link between two cards: card A must finish
 * before card B may start. It is NOT a new field. It already lives in the data
 * model: a card carries `blockerRefs` (jsonb `blocker_refs`, migration 0157), a
 * list of blocker entries, and an entry's `refs` can name another card with
 * `{ kind: 'card', ref: '<CARD-REF>' }`. So "A blocks B" is recorded by B
 * carrying a blockerRef of kind 'card' pointing at A. Read in dependency terms:
 *
 *     A is the PREDECESSOR (it has to finish first)
 *     B is the DEPENDENT / successor (it waits on A)
 *
 * WHY THIS LIVES IN ITS OWN PURE FILE, with no store and no React. The same four
 * questions get asked from the board, the card modal, the Plan and every future
 * schedule view: what does this card wait on, what waits on it, can it start yet,
 * and — when a date moves — what else has to move. Four copies of a graph walk is
 * four places to disagree about whether a cycle is a cycle or whether a dateless
 * card blocks the ones behind it. The rules are written once, here, and proved by
 * scripts/test-dependencies.ts. This is the exact argument src/phases.ts and
 * src/sprints.ts already make for their own rollups.
 *
 * DATES ARE PLAIN `YYYY-MM-DD` STRINGS throughout, and the date maths is reused
 * from src/sprints.ts (`addDays`, `daysBetween`) rather than reinvented — a
 * cascade that crossed a daylight-saving change with raw millisecond arithmetic
 * would land a day early once or twice a year, exactly the bug those helpers were
 * written to avoid. As ISO day strings the dates also compare with `<`/`>` in
 * calendar order, which is the small dividend that keeps the gate checks plain.
 */
import type { IssueStatus, BlockerEntry, DependencyLinkType } from './types';
import { addDays, daysBetween } from './sprints';
import { nextWorkingDay, addWorkingDays, workingDaysInclusive, isWorkingDay, DEFAULT_CALENDAR, type WorkCalendar } from './work-calendar';

/**
 * THE SLICE OF A CARD THIS MODULE READS — its identity, where its work stands,
 * its planned window, and what it is blocked by. A real `Issue` satisfies this
 * structurally (same field names and types), so callers pass Issues straight in;
 * a test fixture only has to carry these few fields. `ref` is the display
 * reference ("#142", "B4") that a blocker names a card by — the stable key the
 * whole graph is built on, NOT the opaque `id`, because the data model links
 * cards by ref.
 */
export interface DepCard {
  id: string;
  ref: string;
  status: IssueStatus;
  startsOn?: string | null;
  dueDate?: string | null;
  blockerRefs?: BlockerEntry[];
}

/** Work that is finished — delivered or deliberately dropped. Either one takes a
 *  card out of "still to do", which is what satisfies a finish-to-start link. The
 *  same two-value rule src/phases.ts uses for a resolved card, kept local so this
 *  module stays self-contained and pure. */
function isResolved(status: IssueStatus): boolean {
  return status === 'done' || status === 'wontdo';
}

/** Whether the card has STARTED — left the two pre-start statuses. The counterpart
 *  to `isResolved` that an `SS`/`SF` link reads: a start-to-start link is met once
 *  the predecessor is under way, not only when it is finished. `backlog`/`todo`
 *  are the not-yet-started statuses; everything from `inprogress` on is started. */
function hasStarted(status: IssueStatus): boolean {
  return status !== 'backlog' && status !== 'todo';
}

/** The first WORKING day strictly after `iso` — the day a plain finish-to-start
 *  hand-off may begin on (never the finish day itself, never a weekend or close). */
function workingDayAfter(iso: string, cal: WorkCalendar): string {
  return nextWorkingDay(addDays(iso, 1), cal);
}

/** Step `n` WORKING days from `iso`, in either direction. `n > 0` runs forward
 *  (the shared `addWorkingDays`), `n < 0` walks back day by day counting only
 *  working days (the lead case — `addWorkingDays` itself floors at zero), and
 *  `n === 0` returns the day unchanged. Lets a link's signed lag/lead shift an
 *  anchor by working days without reinventing the calendar each time. Exported so
 *  the critical path inverts the SAME stepping the cascade applies forward — one
 *  calendar rule, read in both directions, never two that can drift apart. */
export function stepWorkingDays(iso: string, n: number, cal: WorkCalendar): string {
  if (n > 0) return addWorkingDays(iso, n, cal);
  if (n === 0) return iso;
  let cur = iso;
  let left = -n;
  for (let i = 0; i < 36_600 && left > 0; i++) {
    cur = addDays(cur, -1);
    if (isWorkingDay(cur, cal)) left -= 1;
  }
  return cur;
}

/** Whether a date field actually names a day. `startsOn`/`dueDate` are
 *  `string | null | undefined`: null clears the column, absent means unspecified,
 *  and both read the same here — "no day set". An empty string is treated the
 *  same, defensively, so a blank never masquerades as a real date in a compare. */
function hasDay(d: string | null | undefined): d is string {
  return typeof d === 'string' && d.length > 0;
}

/**
 * THE DISTINCT PREDECESSOR CARD-REFS A CARD DEPENDS ON — pulled from its blocker
 * entries' card refs. A blocker entry can name people as well as cards (a person
 * whose input is awaited, #456); only `kind === 'card'` refs are dependencies, so
 * person refs are ignored. The list is:
 *
 *   · DE-DUPED — a card blocked by the same predecessor in two entries waits on
 *     it once, not twice; a graph edge counted twice would double a dependent
 *     count and skew any "how many wait on this" read.
 *   · SELF-STRIPPED — a card naming itself as a blocker is a data error, never a
 *     real wait, and left in it would read as a one-node cycle.
 *   · EMPTY-STRIPPED — a card ref of '' is an entry that names no card yet (the
 *     picker was opened and nothing chosen); it is not a dependency.
 *
 * Order is the order the refs first appear across the entries, which is stable
 * and good enough — nothing downstream relies on predecessor order beyond the
 * topological ordering the cascade computes for itself.
 */
/** One dependency link, read off a card-kind blocker ref: the predecessor it
 *  names, the link `type` (defaulting to `FS`), and its `lag` in working days
 *  (defaulting to 0). This is the typed shape of a predecessor; `predecessorsOf`
 *  is the ref-only view of the same list. */
export interface DepLink {
  ref: string;
  type: DependencyLinkType;
  lag: number;
}

/**
 * THE TYPED DEPENDENCY LINKS A CARD CARRIES — its card-kind blocker refs, each with
 * its link type and lag. Same de-duping as the ref-only `predecessorsOf`: person
 * refs ignored, self-links dropped, empty refs dropped, each predecessor kept once
 * (the first link to it wins its type + lag). `predecessorsOf` is this list mapped
 * to refs, so the two never disagree about what a card depends on.
 */
export function dependencyLinks(card: DepCard): DepLink[] {
  const out: DepLink[] = [];
  const seen = new Set<string>();
  for (const entry of card.blockerRefs ?? []) {
    for (const r of entry.refs ?? []) {
      if (r.kind !== 'card') continue; // a person ref is not a dependency
      const ref = (r.ref ?? '').trim();
      if (!ref) continue; // an entry that names no card yet
      if (ref === card.ref) continue; // a card cannot depend on itself
      if (seen.has(ref)) continue; // the same predecessor, counted once
      seen.add(ref);
      out.push({ ref, type: r.linkType ?? 'FS', lag: r.lag ?? 0 });
    }
  }
  return out;
}

/**
 * THE DISTINCT PREDECESSOR CARD-REFS A CARD DEPENDS ON — the ref-only view of
 * `dependencyLinks`, kept as its own function because the graph, the cycle check
 * and the critical path all reason about edges without caring about type or lag.
 * De-duped, self-stripped and empty-stripped exactly as the typed view is.
 */
export function predecessorsOf(card: DepCard): string[] {
  return dependencyLinks(card).map((l) => l.ref);
}

/**
 * THE DEPENDENCY GRAPH, read in both directions. `predecessors(ref)` is the refs
 * a card waits on; `dependents(ref)` is the refs that wait on it. `refs` is every
 * node in the graph. Lookups return a fresh array (never the internal one), so a
 * caller cannot mutate the graph by pushing into a result.
 */
export interface DepGraph {
  /** Every card ref that is a node in the graph — i.e. present in the input set. */
  readonly refs: string[];
  /** The refs this card waits on THAT EXIST in the set (see buildDepGraph). */
  predecessors(ref: string): string[];
  /** The refs that wait on this card. */
  dependents(ref: string): string[];
}

/**
 * BUILD THE GRAPH from a set of cards, keyed by ref, with both directions filled.
 *
 * ABSENT PREDECESSORS ARE NOT PHANTOM NODES. A card can legitimately name a
 * blocker that is not in the set handed in — a card on another board, a ref typed
 * before its card exists, a card filtered out of the current view. `predecessorsOf`
 * keeps such a ref in the card's OWN predecessor list (it is a real thing the card
 * waits on, and hiding it would make the card look unblocked). But the GRAPH only
 * has nodes for cards that are present: an absent predecessor gets no node, and so
 * creates no edge and appears in nobody's `dependents`. The reason is that the
 * graph's job is to answer "what, among the cards I can see, waits on what" — a
 * dependents map that invented a node for every unseen ref would let a cascade or
 * a cycle check reason about cards it has never been shown, which is exactly the
 * judgement `startGate` refuses to make. So a card's graph `predecessors` is the
 * PRESENT subset of its `predecessorsOf`; the full set stays available by calling
 * `predecessorsOf` directly when the caller wants "everything it waits on".
 */
export function buildDepGraph(cards: DepCard[]): DepGraph {
  const present = new Set(cards.map((c) => c.ref));
  const preds = new Map<string, string[]>();
  const deps = new Map<string, string[]>();
  for (const c of cards) {
    if (!preds.has(c.ref)) preds.set(c.ref, []);
    if (!deps.has(c.ref)) deps.set(c.ref, []);
  }
  for (const c of cards) {
    for (const p of predecessorsOf(c)) {
      if (!present.has(p)) continue; // absent predecessor → no node, no edge
      preds.get(c.ref)!.push(p);
      deps.get(p)!.push(c.ref);
    }
  }
  return {
    refs: [...present],
    predecessors: (ref) => [...(preds.get(ref) ?? [])],
    dependents: (ref) => [...(deps.get(ref) ?? [])],
  };
}

/**
 * WOULD ADDING "dependentRef depends on newPredecessorRef" CREATE A CYCLE — the
 * check the UI runs BEFORE it writes a new dependency, so a loop is refused rather
 * than created. A finish-to-start cycle is unsatisfiable: if A must finish before
 * B and B before A, neither can ever start.
 *
 * It is a cycle when either:
 *   · the two refs are the SAME card (a card cannot wait on itself), or
 *   · newPredecessorRef ALREADY REACHES dependentRef through existing dependency
 *     edges — i.e. newPredecessorRef already (transitively) waits on dependentRef.
 *     Routing dependentRef's wait onto newPredecessorRef would then close the loop.
 *
 * We answer the second by walking the EXISTING predecessor edges up from
 * newPredecessorRef: if that walk ever arrives at dependentRef, the loop exists.
 * The walk uses a visited set, so a graph that is ALREADY malformed (a cycle that
 * somehow got written) cannot make this spin forever — it terminates and reports.
 * Absent predecessors have no outgoing edges and so simply end a branch.
 */
export function wouldCycle(cards: DepCard[], dependentRef: string, newPredecessorRef: string): boolean {
  if (dependentRef === newPredecessorRef) return true; // a self-dependency is a one-node cycle
  const predOf = new Map<string, string[]>();
  for (const c of cards) predOf.set(c.ref, predecessorsOf(c));
  // Walk the predecessor chain up from newPredecessorRef. If it reaches
  // dependentRef, then newPredecessorRef already depends on dependentRef, and the
  // proposed edge would complete a cycle.
  const seen = new Set<string>();
  const stack = [newPredecessorRef];
  while (stack.length) {
    const cur = stack.pop()!;
    if (cur === dependentRef) return true;
    if (seen.has(cur)) continue; // visited — do not re-walk (cycle-safe)
    seen.add(cur);
    for (const p of predOf.get(cur) ?? []) stack.push(p);
  }
  return false;
}

/**
 * THE FINISH-TO-START GATE — can this card start yet, and if not, what is it still
 * waiting on. A predecessor is SATISFIED once it is resolved (done or wontdo): a
 * dropped predecessor releases its dependents just as a delivered one does, the
 * same resolved-means-finished rule the phases use.
 *
 * `openPredecessors` is the card's predecessor refs that EXIST in `byRef` and are
 * NOT yet resolved. `blocked` is simply whether that list is non-empty.
 *
 * A PREDECESSOR NAMED BUT ABSENT FROM `byRef` DOES NOT BLOCK. This is the same
 * refusal buildDepGraph makes: a card we cannot see we cannot judge, and treating
 * every unseen ref as "not yet done" would wedge a card behind a blocker that may
 * long since have shipped on another board. It is safer to let the card start than
 * to hold it on evidence we do not have — and the absent ref is still visible in
 * `predecessorsOf`, so the card's chip can still say it names an off-board wait.
 *
 * This predicate ONLY reports the open predecessors. It does NOT decide whether
 * the block is hard (refuse the start) or soft (warn and allow) — that is the
 * caller's policy, kept out of here so the one rule can serve both.
 */
export function startGate(
  card: DepCard,
  byRef: Map<string, DepCard>,
): { blocked: boolean; openPredecessors: string[] } {
  const openPredecessors: string[] = [];
  for (const link of dependencyLinks(card)) {
    const pred = byRef.get(link.ref);
    if (!pred) continue; // absent — cannot judge, does not block
    // Only the two START-constraining link types can hold a start: FS wants the
    // predecessor FINISHED, SS wants it STARTED. FF and SF constrain this card's
    // FINISH, not its start, so they never gate starting — they surface as a
    // date-conflict on the due, not here. Lag is a date concern (the cascade and
    // the conflict warning), never a status one, so it plays no part in this gate.
    if (link.type === 'FS') {
      if (!isResolved(pred.status)) openPredecessors.push(link.ref);
    } else if (link.type === 'SS') {
      if (!hasStarted(pred.status)) openPredecessors.push(link.ref);
    }
  }
  return { blocked: openPredecessors.length > 0, openPredecessors };
}

/** One card's move in a cascade: where its window was, where it goes, and by how
 *  many days. `byDays` is the whole-day distance the start (and the due, equally)
 *  slid LATER — always positive, because a cascade only ever pushes forward. */
export interface CascadeShift {
  ref: string;
  /** The start/due the card moves FROM and TO. A card that carries only one of the
   *  two dates moves only that one — the other stays `null` both sides, and the
   *  caller patches only the dates that are non-null. `byDays` is always the whole
   *  days the card slid later (measured on whichever date it has). */
  fromStartsOn: string | null;
  toStartsOn: string | null;
  fromDueDate: string | null;
  toDueDate: string | null;
  byDays: number;
}

/**
 * THE FORWARD CASCADE when a predecessor's due date moves LATER. `changedRef`'s
 * due has just become `newDueDate`; this computes which transitive dependents have
 * to move out of its way, and to where, so every finish-to-start link still holds.
 *
 * THE MOVE RULE. A dependent whose start now falls ON OR BEFORE the latest of its
 * predecessors' due dates is in conflict — its predecessor finishes on or after it
 * was due to start. It moves so it starts the first WORKING day after that latest
 * predecessor due (`nextWorkingDay(addDays(latestPredDue, 1), cal)` — never a weekend
 * or a workspace close), and its own due moves to preserve its planned DURATION in
 * WORKING days rather than silently compressing it.
 *
 * DURATION, IN WORKING DAYS (0216, Vinnie 28 Oct). A window [startsOn, dueDate] spans
 * `workingDaysInclusive(startsOn, dueDate, cal)` working days INCLUSIVE (a one-working-
 * day task is one day). The gap from the start to the due day is that minus one, and
 * re-anchoring the start and re-adding that same working-day gap —
 * `toDueDate = addWorkingDays(toStartsOn, gap, cal)` — reproduces a window of the
 * identical WORKING length on the new start day, stepping over any weekend or holiday
 * it now runs into. On the default Mon–Fri calendar with no holidays this is the plain
 * calendar-day behaviour for any window that does not cross a weekend.
 *
 * THE BOUNDARIES, each one a deliberate decision:
 *
 *   · FORWARD ONLY. A dependent is never pulled EARLIER. If it already starts
 *     after the new predecessor finish there is no conflict, so it does not move —
 *     and, because its own due is then unchanged, nothing it feeds moves THROUGH
 *     it either. Slack ahead of a dependency is the dependent's to keep.
 *
 *   · A DATELESS DEPENDENT STOPS ITS BRANCH. A card missing a start OR a due has
 *     no window to slide and no duration to preserve, so it cannot be shifted —
 *     it is skipped, with no CascadeShift. And because it has no finish of its
 *     own to impose, it pushes nothing: the cascade does not travel on through it.
 *     Its further dependents can still move if some OTHER, dated predecessor of
 *     theirs moved — a dateless card simply contributes no constraint, it does not
 *     veto one.
 *
 *   · THE LATEST PREDECESSOR DRIVES. A card with several predecessors must clear
 *     the LAST of them to finish, so the driver is the maximum of its present
 *     predecessors' (possibly already-shifted) due dates. Predecessors with no due
 *     impose no finish and are ignored in that maximum.
 *
 *   · CYCLE-SAFE. Reachability and ordering both use visited sets, so a malformed
 *     cycle in the data cannot make this loop forever; nodes a cycle leaves
 *     unresolved are simply not emitted.
 *
 * The result is in TOPOLOGICAL ORDER: a card always appears after the predecessor
 * whose move pushed it, so a caller can apply the shifts top to bottom and read
 * them as a chain. `changedRef` itself is never in the list — its due moved by the
 * caller's own action, not by this cascade.
 */
export function cascadeShifts(
  cards: DepCard[],
  changedRef: string,
  change: string | { startsOn?: string | null; dueDate?: string | null },
  cal: WorkCalendar = DEFAULT_CALENDAR,
): CascadeShift[] {
  // The change is the driver's new window. A bare string is its new DUE (the common
  // edit, and what every caller before link types passed); the object form also
  // carries a new START, so a move of the predecessor's start propagates SS/SF links
  // just as a due move propagates FS/FF. An absent field means that date did not move.
  const moved = typeof change === 'string' ? { dueDate: change } : change;
  const present = new Set(cards.map((c) => c.ref));
  if (!present.has(changedRef)) return []; // nothing here depends on a card we don't hold

  const graph = buildDepGraph(cards);
  const byRef = new Map(cards.map((c) => [c.ref, c] as const));

  // Each card's effective (possibly already-shifted) window — start and due kept
  // separately, because a link reads whichever end its type names: FS/FF key off a
  // predecessor's DUE, SS/SF off its START. Updated as cards move, so a dependent
  // reads its predecessors' POST-move dates. A card missing a date is absent from
  // that map and imposes no constraint through it. The driver's new due is laid
  // over the top; its start is unchanged (only the due moved).
  const effStart = new Map<string, string>();
  const effDue = new Map<string, string>();
  for (const c of cards) {
    if (hasDay(c.startsOn)) effStart.set(c.ref, c.startsOn);
    if (hasDay(c.dueDate)) effDue.set(c.ref, c.dueDate);
  }
  if (hasDay(moved.startsOn)) effStart.set(changedRef, moved.startsOn);
  if (hasDay(moved.dueDate)) effDue.set(changedRef, moved.dueDate);

  // The set of cards reachable FORWARD from the driver — the only cards a move of
  // the driver can possibly touch. A visited set keeps a malformed cycle finite.
  // The driver itself is excluded: it does not move, it only pushes.
  const reachable = new Set<string>();
  const queue = [...graph.dependents(changedRef)];
  while (queue.length) {
    const ref = queue.shift()!;
    if (reachable.has(ref) || ref === changedRef) continue;
    reachable.add(ref);
    for (const d of graph.dependents(ref)) queue.push(d);
  }

  // Order the reachable cards topologically, so each is processed only after every
  // predecessor that could push it is final. Kahn's algorithm, counting only the
  // predecessors that are THEMSELVES reachable (a predecessor outside the reachable
  // set — including the driver — is already final and does not gate ordering).
  const indeg = new Map<string, number>();
  for (const ref of reachable) {
    const gating = graph.predecessors(ref).filter((p) => reachable.has(p));
    indeg.set(ref, gating.length);
  }
  const ready = [...reachable].filter((ref) => (indeg.get(ref) ?? 0) === 0);
  const order: string[] = [];
  while (ready.length) {
    const ref = ready.shift()!;
    order.push(ref);
    for (const d of graph.dependents(ref)) {
      if (!reachable.has(d)) continue;
      const n = (indeg.get(d) ?? 0) - 1;
      indeg.set(d, n);
      if (n === 0) ready.push(d);
    }
  }
  // Any reachable card not in `order` sits on a cycle and is deliberately left out
  // rather than guessed at — terminating safely, per the cycle-safe boundary.

  const shifts: CascadeShift[] = [];
  const later = (cur: string | undefined, cand: string) => (cur === undefined || cand > cur ? cand : cur);
  for (const ref of order) {
    const card = byRef.get(ref)!;
    const hasStart = hasDay(card.startsOn);
    const hasDue = hasDay(card.dueDate);

    // A dependent with NO date at all cannot slide and imposes no finish on anything
    // behind it: skip it, its branch stops here. (A card with EITHER date still moves
    // — the common case is a due date and no explicit start.)
    if (!hasStart && !hasDue) continue;

    // THE FLOORS this card's dates must clear, gathered across every present, dated
    // predecessor link. A link constrains either this card's START (FS/SS) or its
    // DUE (FF/SF), anchored on the predecessor's finish (FS/FF) or start (SS/SF) and
    // shifted by the link's working-day lag/lead. The driver is the LATEST such floor.
    let startFloor: string | undefined; // this card's start must be >= this
    let dueFloor: string | undefined; //   this card's due must be >= this
    for (const link of dependencyLinks(card)) {
      const pStart = effStart.get(link.ref); // absent predecessor (or no such date) → no constraint
      const pDue = effDue.get(link.ref);
      switch (link.type) {
        case 'FS': // finish → start: begin the working day after the finish, plus lag
          if (pDue) startFloor = later(startFloor, stepWorkingDays(workingDayAfter(pDue, cal), link.lag, cal));
          break;
        case 'SS': // start → start: begin no earlier than the predecessor's start, plus lag
          if (pStart) startFloor = later(startFloor, stepWorkingDays(pStart, link.lag, cal));
          break;
        case 'FF': // finish → finish: finish no earlier than the predecessor's finish, plus lag
          if (pDue) dueFloor = later(dueFloor, stepWorkingDays(pDue, link.lag, cal));
          break;
        case 'SF': // start → finish: finish no earlier than the predecessor's start, plus lag
          if (pStart) dueFloor = later(dueFloor, stepWorkingDays(pStart, link.lag, cal));
          break;
      }
    }
    if (startFloor === undefined && dueFloor === undefined) continue; // nothing constrains it

    if (hasStart && hasDue) {
      // BOTH dates — slide forward to clear both floors, keeping the planned duration
      // in WORKING days (Vinnie, 28 Oct) so a five-working-day window stays five,
      // stepping over any weekend or holiday the new anchor meets. A due floor is met
      // by backing the duration off it; the later of the two drives. Forward-only: a
      // card with slack ahead of every floor does not move.
      const gap = Math.max(0, workingDaysInclusive(card.startsOn!, card.dueDate!, cal) - 1);
      let newStart = card.startsOn!;
      if (startFloor && startFloor > newStart) newStart = startFloor;
      if (dueFloor) {
        const impliedStart = stepWorkingDays(dueFloor, -gap, cal); // the latest start whose due still clears the floor
        if (impliedStart > newStart) newStart = impliedStart;
      }
      if (newStart <= card.startsOn!) continue; // forward only — never pulled earlier
      const toDueDate = addWorkingDays(newStart, gap, cal); // same working duration, new anchor
      shifts.push({
        ref,
        fromStartsOn: card.startsOn!,
        toStartsOn: newStart,
        fromDueDate: card.dueDate!,
        toDueDate,
        byDays: daysBetween(card.startsOn!, newStart) - 1,
      });
      effStart.set(ref, newStart);
      effDue.set(ref, toDueDate); // its moved window drives cards behind it
    } else if (hasDue) {
      // DUE ONLY (the common case) — only a finish to move. A start-constraining floor
      // (FS/SS) still binds the single date: the finish cannot precede where the start
      // would have to be. The due moves to the later of the two floors if it falls short.
      let newDue = card.dueDate!;
      if (dueFloor && dueFloor > newDue) newDue = dueFloor;
      if (startFloor && startFloor > newDue) newDue = startFloor;
      if (newDue <= card.dueDate!) continue;
      shifts.push({
        ref,
        fromStartsOn: null,
        toStartsOn: null,
        fromDueDate: card.dueDate!,
        toDueDate: newDue,
        byDays: daysBetween(card.dueDate!, newDue) - 1,
      });
      effDue.set(ref, newDue); // its moved finish drives cards behind it
    } else {
      // START ONLY — push the start to its floor; a due floor has no due of its own to
      // bind. It carries no finish, so it drives nothing behind it (no effDue set).
      if (startFloor === undefined || startFloor <= card.startsOn!) continue;
      shifts.push({
        ref,
        fromStartsOn: card.startsOn!,
        toStartsOn: startFloor,
        fromDueDate: null,
        toDueDate: null,
        byDays: daysBetween(card.startsOn!, startFloor) - 1,
      });
      effStart.set(ref, startFloor);
    }
  }

  return shifts;
}

/**
 * THE MANUAL DATE-CONFLICT WARNING — a soft check (never a block) for the card
 * modal's date field. When someone sets this card's start on or before where a
 * predecessor link requires it, this names the binding predecessor and the start
 * that would clear it, so the field can offer a one-click "Move to <date>".
 *
 * It reads the SAME floors the cascade does, but only the START-constraining links
 * (FS/SS) and only the card's own start — the common "I typed a start that clashes
 * with what this waits on" case the mock shows. Returns the EARLIEST conflicting
 * floor (the one the start must clear first), or `null` when the start already
 * clears every present predecessor. Absent predecessors impose nothing, exactly as
 * the gate and the cascade refuse to judge a card they cannot see; a predecessor that
 * is already resolved (done/wontdo) imposes nothing either — its planned date no
 * longer binds work that has happened.
 */
export interface DateConflict {
  /** The predecessor whose link the start violates. */
  predecessorRef: string;
  /** That predecessor's anchor date — its finish for an FS link, its start for SS. */
  predecessorAnchor: string;
  /** The first start that clears the link (the field's "Move to <date>"). */
  suggestedStart: string;
}

export function dateConflict(
  card: DepCard,
  byRef: Map<string, DepCard>,
  cal: WorkCalendar = DEFAULT_CALENDAR,
): DateConflict | null {
  if (!hasDay(card.startsOn)) return null; // no start set → nothing to clash
  let worst: DateConflict | null = null;
  for (const link of dependencyLinks(card)) {
    const pred = byRef.get(link.ref);
    if (!pred) continue; // absent — cannot judge
    if (isResolved(pred.status)) continue; // already finished — its planned date no longer binds
    let floor: string | undefined;
    let anchor: string | undefined;
    if (link.type === 'FS' && hasDay(pred.dueDate)) {
      anchor = pred.dueDate;
      floor = stepWorkingDays(workingDayAfter(pred.dueDate, cal), link.lag, cal);
    } else if (link.type === 'SS' && hasDay(pred.startsOn)) {
      anchor = pred.startsOn;
      floor = stepWorkingDays(pred.startsOn, link.lag, cal);
    }
    if (!floor || !anchor) continue;
    if (card.startsOn >= floor) continue; // this start already clears the link
    // Keep the LATEST floor — the start has to clear the furthest-out predecessor.
    if (!worst || floor > worst.suggestedStart) {
      worst = { predecessorRef: link.ref, predecessorAnchor: anchor, suggestedStart: floor };
    }
  }
  return worst;
}
